import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { AppRole, DepartmentType } from './useUserRole';
import { queryKeys } from '@/lib/queryKeys';

export interface TeamMemberWithRole {
  id: string;
  email: string;
  full_name: string | null;
  avatar_url: string | null;
  role: AppRole | null;
  department: DepartmentType | null;
  created_at: string;
  last_sign_in_at: string | null;
  app_access_count?: number;
  has_all_apps_access?: boolean;
}

/**
 * ✅ Migrated to React Query - automatic caching for team members
 */
async function fetchTeamMembers() {
  // Use security definer function - only admins/managers can see all team members
  const { data: profiles, error: profilesError } = await supabase
    .rpc('get_team_members');

  if (profilesError) throw profilesError;
  if (!profiles || profiles.length === 0) return [];

  // Get all user roles (RLS may hide rows for non-admins)
  const { data: roles, error: rolesError } = await supabase
    .from('user_roles')
    .select('user_id, role, department');

  if (rolesError) throw rolesError;

  // Get app access counts for all users
  const { data: appAccessData, error: appAccessError } = await supabase
    .from('user_app_access')
    .select('user_id, has_all_apps_access, app_id');

  if (appAccessError) {
    console.error('Error fetching app access:', appAccessError);
  }

  // Build app access map
  const appAccessMap = new Map<string, { count: number; hasAll: boolean }>();
  if (appAccessData && appAccessData.length > 0) {
    appAccessData.forEach((access: any) => {
      if (access.has_all_apps_access) {
        appAccessMap.set(access.user_id, { count: -1, hasAll: true });
      } else if (access.app_id) {
        const existing = appAccessMap.get(access.user_id) || { count: 0, hasAll: false };
        if (!existing.hasAll) {
          appAccessMap.set(access.user_id, { count: existing.count + 1, hasAll: false });
        }
      }
    });
  }

  // Build a roles map
  const rolesMap = new Map<string, { role: AppRole | null; department: DepartmentType | null }>();
  (roles || []).forEach((r: any) => {
    rolesMap.set(r.user_id, { role: (r.role as AppRole) ?? null, department: (r.department as DepartmentType) ?? null });
  });

  // Fallback: if some profiles don't have a visible role due to RLS, fetch via RPCs
  const missingProfiles = (profiles || []).filter((p: any) => !rolesMap.has(p.id));
  if (missingProfiles.length > 0) {
    const fallbacks = await Promise.all(
      missingProfiles.map(async (p: any) => {
        const [roleRes, deptRes] = await Promise.all([
          supabase.rpc('get_user_role', { _user_id: p.id }),
          supabase.rpc('get_user_department', { _user_id: p.id })
        ]);
        return {
          user_id: p.id,
          role: (roleRes.data as AppRole | null) ?? null,
          department: (deptRes.data as DepartmentType | null) ?? null
        };
      })
    );
    fallbacks.forEach((r) => rolesMap.set(r.user_id, { role: r.role, department: r.department }));
  }

  // Merge profiles with roles
  const membersWithRoles: TeamMemberWithRole[] = profiles.map(profile => {
    const userRole = rolesMap.get(profile.id);
    const appAccess = appAccessMap.get(profile.id);
    
    // Special case: QA user
    if (profile.email === 'qa.weewoo@gmail.com') {
      console.log('[useTeamMembersWithRoles] QA user found in profiles');
      return {
        id: profile.id,
        email: profile.email,
        full_name: profile.full_name || 'QA Weewoo',
        avatar_url: profile.avatar_url,
        role: 'qa' as AppRole,
        department: null,
        created_at: profile.created_at,
        last_sign_in_at: profile.last_sign_in_at,
        app_access_count: appAccess?.hasAll ? -1 : (appAccess?.count || 0),
        has_all_apps_access: appAccess?.hasAll || false
      };
    }
    
    return {
      id: profile.id,
      email: profile.email,
      full_name: profile.full_name,
      avatar_url: profile.avatar_url,
      role: userRole?.role ?? null,
      department: userRole?.department ?? null,
      created_at: profile.created_at,
      last_sign_in_at: profile.last_sign_in_at,
      app_access_count: appAccess?.hasAll ? -1 : (appAccess?.count || 0),
      has_all_apps_access: appAccess?.hasAll || false
    };
  });

  return membersWithRoles;
}

export function useTeamMembersWithRoles() {
  const queryClient = useQueryClient();
  
  // ✅ React Query with 5 min cache for stable team data
  const { data: teamMembers = [], isLoading: loading, error } = useQuery({
    queryKey: queryKeys.users.teamMembers(),
    queryFn: fetchTeamMembers,
    staleTime: 5 * 60 * 1000, // 5 min - team data is stable
    gcTime: 30 * 60 * 1000, // 30 min cache
  });

  // ✅ Delete team member mutation
  const deleteTeamMember = async (userId: string): Promise<{ success: boolean; error?: string }> => {
    try {
      // Call the RPC function to delete user completely
      const { error: deleteError } = await supabase.rpc('delete_user_completely', {
        user_id: userId
      });

      if (deleteError) throw deleteError;

      // Invalidate queries to refresh the list
      await queryClient.invalidateQueries({ queryKey: queryKeys.users.teamMembers() });

      return { success: true };
    } catch (err) {
      console.error('Error deleting team member:', err);
      return { 
        success: false, 
        error: err instanceof Error ? err.message : 'Error al eliminar usuario'
      };
    }
  };

  return {
    teamMembers,
    loading,
    error: error ? String(error) : null,
    refresh: () => queryClient.invalidateQueries({ queryKey: queryKeys.users.teamMembers() }),
    deleteTeamMember,
  };
}
